"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

import { isSensitiveRoute } from "@/lib/sensitive-routes";

type DeferredMarketingTagsProps = {
  gtmId: string;
  metaPixelId: string;
};

/**
 * Loads the marketing tag libraries (Meta pixel, GTM) on first user
 * interaction, or 5s after window load as a fallback.
 *
 * The previous lazyOnload inline snippets still downloaded ~250KB of
 * third-party JS inside Lighthouse's trace window (fbevents + config),
 * costing TBT and bandwidth during the LCP-critical phase on slow
 * connections. Queue stubs are installed immediately on mount, so any
 * fbq()/dataLayer events fired before the libraries arrive are retained
 * and flushed when they load: no data loss, just a later dispatch.
 *
 * Measurement tradeoff (intentional): visits that bounce with zero
 * interaction in under ~5s after load report late or not at all to the
 * pixel. Ad click-through conversions are unaffected (a conversion
 * requires interaction by definition, and gclid/fbclid attribution comes
 * from the landing URL).
 */
export function DeferredMarketingTags({
  gtmId,
  metaPixelId,
}: DeferredMarketingTagsProps) {
  const pathname = usePathname();

  useEffect(() => {
    if (isSensitiveRoute(pathname)) return;
    if (!gtmId && !metaPixelId) return;

    const w = window as Window & {
      dataLayer?: unknown[];
      fbq?: unknown;
      _fbq?: unknown;
    };

    w.dataLayer = w.dataLayer || [];

    if (metaPixelId && !w.fbq) {
      // Meta's canonical queue stub, minus the script injection (deferred
      // below). Calls queue in n.queue and flush when fbevents.js loads.
      type FbqStub = {
        (...args: unknown[]): void;
        callMethod?: (...args: unknown[]) => void;
        queue: unknown[];
        push: unknown;
        loaded: boolean;
        version: string;
      };
      // Meta's canonical snippet applies callMethod on the closure-captured
      // queue object `n`, never on `this` (call sites vary between fbq(...),
      // window.fbq(...), and fbq.push(...), so `this` is unreliable).
      const n: FbqStub = function (...args: unknown[]) {
        if (n.callMethod) {
          n.callMethod(...args);
        } else {
          n.queue.push(args);
        }
      } as FbqStub;
      n.queue = [];
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      w.fbq = n;
      if (!w._fbq) w._fbq = n;
      (w.fbq as FbqStub)("init", metaPixelId);
      (w.fbq as FbqStub)("track", "PageView");
    }

    let injected = false;
    let fallbackTimer: number | undefined;
    const interactionEvents = [
      "pointerdown",
      "keydown",
      "touchstart",
      "wheel",
    ] as const;

    const cleanup = () => {
      interactionEvents.forEach((event) =>
        window.removeEventListener(event, inject),
      );
      window.removeEventListener("load", armFallback);
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
    };

    const inject = () => {
      if (injected) return;
      injected = true;
      cleanup();

      if (metaPixelId) {
        const script = document.createElement("script");
        script.async = true;
        script.src = "https://connect.facebook.net/en_US/fbevents.js";
        document.head.appendChild(script);
      }

      if (gtmId) {
        w.dataLayer!.push({ "gtm.start": Date.now(), event: "gtm.js" });
        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
        document.head.appendChild(script);
      }
    };

    const armFallback = () => {
      fallbackTimer = window.setTimeout(inject, 5000);
    };

    interactionEvents.forEach((event) =>
      window.addEventListener(event, inject, { once: true, passive: true }),
    );
    if (document.readyState === "complete") {
      armFallback();
    } else {
      window.addEventListener("load", armFallback, { once: true });
    }

    return cleanup;
  }, [gtmId, metaPixelId, pathname]);

  return null;
}
